import React, { useState, useEffect, useRef, useCallback } from 'react';

const COLORS = {
  labelColor: '#616E7C',
  titleColor: '#1F2933',
  subtle: '#9AA5B4',
  border: '#CBD2D9',
};

interface Category {
  name: string;
  amount: number;
  color: string;
  // Relative visual weight (from SVG reference, not strictly proportional to amount)
  weight: number;
}

const CATEGORIES: Category[] = [
  { name: 'Technology', amount: 392, color: '#3E19A4', weight: 354 },
  { name: 'Travel',     amount: 232, color: '#4A3CF3', weight: 345 },
  { name: 'Tools',      amount: 170, color: '#2169AC', weight: 215 },
  { name: 'News',       amount: 27,  color: '#8D63FF', weight: 109 },
  { name: 'Social',     amount: 22,  color: '#9432F5', weight: 113 },
];

const TOTAL_WEIGHT = CATEGORIES.reduce((s, c) => s + c.weight, 0);

const ANIM_DURATION = 2000;

// Per-bar spring traits: [overshootPct, frequency, damping]
// Each bar grows with damped oscillation; windowed so all bars arrive
// at their final width smoothly, with zero velocity at t=1.
const SPRING_TRAITS: [number, number, number][] = [
  [0.22, 2.6, 3.2],   // Technology — big bar, visible overshoot
  [0.20, 2.9, 3.2],   // Travel
  [0.18, 3.3, 3.4],   // Tools
  [0.32, 3.8, 2.8],   // News — small bar, bouncier
  [0.36, 4.1, 3.0],   // Social — small bar, bounciest
];

// Damped spring growth with guaranteed smooth arrival.
// At t=1: value = 1 exactly, slope = 0 (zero residual velocity).
// This eliminates the end-jerk caused by RAF clamping while the spring
// still has oscillation energy.
function springGrow(t: number, overshoot: number, freq: number, damp: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;

  // Base: quartic ease-out — hits 1 exactly at t=1 with zero slope
  const base = 1 - Math.pow(1 - t, 4);

  // Bounce: damped sine, windowed by (1-t)² so both amplitude AND velocity
  // fade to zero at t=1. Squared window gives C¹ continuity at the boundary.
  const envelope = Math.exp(-damp * t);
  const windowFade = (1 - t) * (1 - t);
  const oscillation = Math.sin(t * freq * Math.PI * 2);
  const bounce = envelope * windowFade * oscillation * overshoot;

  return base + bounce;
}

// Get animated weight for bar i at progress t (0→1)
// Bars grow directly to their final proportions — no equal-width phase
function getAnimatedWeight(i: number, t: number): number {
  const target = CATEGORIES[i].weight;
  const [overshoot, freq, damp] = SPRING_TRAITS[i];
  return target * Math.max(0, springGrow(t, overshoot, freq, damp));
}

const ExpensesIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17 9V7C17 5.89543 16.1046 5 15 5H5C3.89543 5 3 5.89543 3 7V13C3 14.1046 3.89543 15 5 15H7M9 19H19C20.1046 19 21 18.1046 21 17V11C21 9.89543 20.1046 9 19 9H9C7.89543 9 7 9.89543 7 11V17C7 18.1046 7.89543 19 9 19ZM16 14C16 15.1046 15.1046 16 14 16C12.8954 16 12 15.1046 12 14C12 12.8954 12.8954 12 14 12C15.1046 12 16 12.8954 16 14Z"
      stroke="#111827"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const SubscriptionsChart: React.FC = () => {
  const [rawProgress, setRawProgress] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const startTimeRef = useRef<number>(0);
  const rafRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isAnimating = rawProgress < 1;

  const animate = useCallback((timestamp: number) => {
    if (!startTimeRef.current) startTimeRef.current = timestamp;
    const elapsed = timestamp - startTimeRef.current;
    const t = Math.min(1, elapsed / ANIM_DURATION);
    setRawProgress(t);
    if (t < 1) {
      rafRef.current = requestAnimationFrame(animate);
    }
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [animate]);

  const handleMouseEnter = (index: number, e: React.MouseEvent) => {
    if (isAnimating) return;
    setHoveredIndex(index);
    updateTooltipPos(e);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isAnimating) return;
    updateTooltipPos(e);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
    setTooltipPos(null);
  };

  const updateTooltipPos = (e: React.MouseEvent) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  const totalAmount = CATEGORIES.reduce((s, c) => s + c.amount, 0);

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '8px',
        border: `1px solid ${COLORS.border}`,
        padding: '20px 24px 24px',
        width: '100%',
        maxWidth: 'var(--chart-max-width, 1180px)',
        boxSizing: 'border-box' as const,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ExpensesIcon />
          <span
            style={{
              fontSize: '16px',
              fontWeight: 400,
              color: COLORS.titleColor,
              letterSpacing: '-0.01em',
            }}
          >
            Top expenses
          </span>
        </div>
        <span style={{ fontSize: 13, color: COLORS.subtle, fontWeight: 500 }}>
          Monthly
        </span>
      </div>

      {/* Bar chart */}
      <div
        ref={containerRef}
        style={{ position: 'relative' }}
      >
        <style>{`
          .expense-bar-idle {
            transition: opacity 0.25s ease, filter 0.25s ease;
          }
        `}</style>
        <div
          style={{
            display: 'flex',
            gap: '2px',
            height: '50px',
            overflow: 'hidden',
          }}
        >
          {CATEGORIES.map((cat, i) => {
            // Each bar independently grows from 0% → its final target %
            // Normalize by the FIXED target total, not the changing animated total,
            // so bars stretch smoothly toward their final widths without
            // visual "stalling" when sibling bars change ratios.
            const animWeight = Math.max(0, getAnimatedWeight(i, rawProgress));
            const pct = (animWeight / TOTAL_WEIGHT) * 100;
            const isDimmed = hoveredIndex !== null && hoveredIndex !== i;
            // Stagger fade + slide per bar for left-to-right reveal
            const barFadeDelay = i * 0.04;
            const barFade = Math.min(1, Math.max(0, (rawProgress - barFadeDelay) / 0.18));
            const barFadeEased = 1 - Math.pow(1 - barFade, 3);
            const barOpacity = isDimmed ? 0.35 : barFadeEased;
            const slideOffset = (1 - barFadeEased) * -20;

            // Labels appear once bars are mostly settled
            const labelReady = rawProgress > 0.4;

            return (
              <div
                key={i}
                className={isAnimating ? 'expense-bar' : 'expense-bar expense-bar-idle'}
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: cat.color,
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  cursor: isAnimating ? 'default' : 'pointer',
                  opacity: barOpacity,
                  transform: `translateX(${slideOffset}px)`,
                }}
                onMouseEnter={(e) => handleMouseEnter(i, e)}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              >
                {labelReady && (
                  <span
                    style={{
                      color: 'white',
                      fontSize: 13,
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                      whiteSpace: 'nowrap',
                      opacity: Math.min(1, (rawProgress - 0.5) / 0.2),
                      fontFamily: "'Inter', -apple-system, sans-serif",
                    }}
                  >
                    {cat.name} <span style={{ fontWeight: 700 }}>${cat.amount}</span>
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Tooltip */}
        {!isAnimating && hoveredIndex !== null && tooltipPos && (() => {
          const cat = CATEGORIES[hoveredIndex];
          const pctOfTotal = ((cat.amount / totalAmount) * 100).toFixed(1);
          const containerW = containerRef.current?.clientWidth ?? 1000;
          const tipLeft = Math.min(Math.max(tooltipPos.x + 16, 0), containerW - 220);
          return (
            <div
              style={{
                position: 'absolute',
                left: tipLeft,
                top: tooltipPos.y - 80,
                background: 'white',
                borderRadius: '10px',
                padding: '14px 16px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)',
                border: '1px solid #E4E7EB',
                minWidth: '180px',
                fontFamily: "'Inter', -apple-system, sans-serif",
                pointerEvents: 'none',
                zIndex: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: cat.color, flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: COLORS.titleColor, fontWeight: 600 }}>
                  {cat.name}
                </span>
              </div>
              <div style={{ fontSize: 14, color: COLORS.titleColor, marginBottom: 4 }}>
                <strong>${cat.amount}</strong> / month
              </div>
              <div style={{ fontSize: 13, color: COLORS.subtle }}>
                {pctOfTotal}% of total expenses
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};
